import * as React from "react";

import "./remove-bg-mode.scss";

import {bindInput}                    from "@Common/react/bind-input";
import {cst, cstAssign}               from "@Common/react/chain-services";
import {consumeContext}               from "@Common/react/context";
import {v}                            from "@Common/react/cst2";
import {Invoke}                       from "@Common/react/invoke";
import {Load2}                        from "@Common/react/load2";
import {scopedState}                  from "@Common/react/scoped-state";
import {scn}                          from "@Common/react/state-change-nested-value";
import {UseState}                     from "@Common/react/use-state";
import {Types}                        from "@Common/types";
import {addURLParameterToWindow}      from "@Common/utils/add-url-parameter";
import {removeURLParameterFromWindow} from "@Common/utils/remove-url-parameter";

import {PREVIEW_REMOVE_BACKGROUND, REMOVE_BACKGROUND} from "../../../../common/logic/cutoutpro";
import {ClearableSelect}                              from "../clearable-select/clearable-select";

type Form = Omit<Types.Apis.Payloads.Next.Gallery.HeadshotsFromCutoutProRequest, "idImageAsset"> & {isSaving: boolean};
const MSG_API_ERROR = "This service is temporarily unavailable. Please try again later!";

export const RemoveBackgroundMode = (
    {
        cancel,
        closeModal,
        headshot,
        onDone,
        agentId,
        typeLabel,
        allowDefaultOption = true,
        next
    }:{
        agentId: string,
        allowDefaultOption : boolean|number,
        typeLabel        : string,
        cancel           : () => void,
        closeModal       : () => void,
        headshot         : {id: number, name: string, defaultSlot: 0 | 1 | 2},
        next             : (v:Types.Xpresso.Account.Common.HeadshotsPanel.EditModal.ContentNext) => any,
        onDone           : (res:any) => void,
    }
) => cst<{
    apis             : Types.Apis.Loaders.XpressoLoadApis,
    formState        : Types.Common.React.UseState.UseStateNext<Form>,
    previewLoad      : Types.Common.React.Load2.Load2Next<Types.Apis.Models.Cutoutpro>,
}>(
    consumeContext("apis"),
    ({}, next) => Invoke({action: () => addURLParameterToWindow({parameter: "action", value: "remove-background"}), next}),
    cstAssign("previewLoad", ({apis}, next) => Load2({
        fetch: async ():Promise<Types.Apis.Models.Cutoutpro> => {
            try {
                return await apis.next.gallery.headshots.asCutoutPro({
                    id: headshot.id,
                    idCutoutproOperation: PREVIEW_REMOVE_BACKGROUND,
                }) as Types.Apis.Models.Cutoutpro;
            } catch (error) {
                window.alert(MSG_API_ERROR);
                cancel();
                removeURLParameterFromWindow({parameter: "action"});
                throw error;
            }
        },
        next,
    })),
    cstAssign("formState", ({}, next) => UseState({next, initValue: {defaultSlot: headshot.defaultSlot, isSaving: false, name: `${headshot.name} no background`} as Form})),
    ({apis, formState, previewLoad}) => next({
        isDisabled : previewLoad.loading || formState.value.isSaving || formState.value.name.length < 1,
        onCancel   : () => {
            removeURLParameterFromWindow({parameter: "action"});
            cancel();
        },
        onSave     : async () => {
            scn(formState, ["isSaving"], ({}) => true);

            try {

                const res = await apis.next.gallery.headshots.asCutoutPro({id: headshot.id, idCutoutproOperation: REMOVE_BACKGROUND}).then((res) => {
                    return "success" in res ? Promise.reject() : apis.next.gallery.headshots.fromCutoutPro({
                        idImageAsset: res.idImageAsset, ...formState.value,
                        idAgent: agentId,
                    });
                });

                if ("success" in res)
                    return Promise.reject()
                else {
                    removeURLParameterFromWindow({parameter: "action"});
                    closeModal();
                    onDone(res);
                }
            } catch (e) {

                scn(formState, ["isSaving"], ({}) => false);
                window.alert(MSG_API_ERROR);
            }
        },
        render     : () => <div className="remove-background-mode-9rko">
            <div className="preview-img-container">
                {previewLoad.loading ? (
                    <span xd-qa-type="label" xd-qa-name="Generating preview...">Generating preview...</span>
                ) : (
                    <img xd-qa-type="image" xd-qa-name="Preview Image" src={previewLoad.value.printUrl} alt="Image Preview"/>
                )}
            </div>
            <div className="form">
                <label
                    htmlFor="name" xd-qa-type="label" xd-qa-name={`Name the new ${typeLabel}`} className="required"
                >
                    {`Name the new ${typeLabel}`}
                </label>
                <input
                    className="name"
                    {...bindInput(scopedState(formState, ["name"]))}
                    xd-qa-type="text_field"
                    xd-qa-name="Name the new headshot"
                />
                {!!allowDefaultOption && ClearableSelect({
                    list: allowDefaultOption === 1 ? [1] : [1, 2],
                    placeholder: "Make Default",
                    xdQaName: "Make Default",
                    isSelected: v => v === formState.value.defaultSlot,
                    onSelect: (index:number) => scn(formState, ["defaultSlot"], ({}) => index),
                    onClear: () => scn(formState, ["defaultSlot"], ({}) => 0),

                    getToggleLabel: i => "Default " + typeLabel.replace("Default ", "") + " #" + i,
                    getExpandLabel: i => typeLabel + " #" + i,
                })}
            </div>
        </div>,
        title      : "Preview",
    }),
);
